import React from 'react';
import { Avatar, Box, Typography, Skeleton } from "@mui/material";
import { User, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";

const GOLD = "#FFBD65";
const TEXT_PRI = "#333333";
const TEXT_SEC = "#555555";
const ACTIVE_BG = "rgba(255, 189, 101, 0.08)";
const BORDER = "rgba(255, 189, 101, 0.2)";

const SidebarProfile = ({ collapsed, onClose }) => {
    const navigate = useNavigate();
    const { user, loading } = useUser();

    const handleClick = () => {
        navigate('/profile');
        if (onClose) onClose();
    };

    // ── Collapsed: just avatar ──────────────────────────────────
    if (collapsed) {
        return (
            <Box
                onClick={handleClick}
                title={user?.name || 'Profile'}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 0.5,
                    cursor: 'pointer',
                }}
            >
                <Avatar
                    sx={{
                        width: 28,
                        height: 28,
                        bgcolor: GOLD,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#1a1a1a',
                        border: `1.5px solid ${BORDER}`,
                        transition: 'border-color 0.15s',
                        '&:hover': { borderColor: GOLD },
                    }}
                >
                    {loading
                        ? <User size={13} />
                        : (user?.name ? user.name.charAt(0).toUpperCase() : <User size={13} />)
                    }
                </Avatar>
            </Box>
        );
    }

    // ── Expanded ────────────────────────────────────────────────
    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: '8px',
                bgcolor: ACTIVE_BG,
                border: `1px solid ${BORDER}`,
                cursor: 'pointer',
            }}
        >
            {/* Avatar */}
            <Avatar
                sx={{
                    width: 30,
                    height: 30,
                    bgcolor: GOLD,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    flexShrink: 0,
                }}
            >
                {loading
                    ? <User size={14} />
                    : (user?.name ? user.name.charAt(0).toUpperCase() : <User size={14} />)
                }
            </Avatar>

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {loading ? (
                    <>
                        <Skeleton variant="text" width={80} height={13} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '4px', mb: 0.3 }} />
                        <Skeleton variant="text" width={100} height={10} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '4px' }} />
                    </>
                ) : (
                    <>
                        <Typography
                            sx={{
                                fontSize: '0.74rem',
                                fontWeight: 600,
                                color: TEXT_PRI,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.25,
                            }}
                        >
                            {user?.name || 'User'}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.65rem',
                                color: TEXT_SEC,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.3,
                                display: 'block',
                            }}
                        >
                            {user?.email || 'Admin'}
                        </Typography>
                    </>
                )}
            </Box>

            {/* Arrow hint */}
            <ChevronRight size={12} color={TEXT_SEC} style={{ flexShrink: 0 }} />
        </Box>
    );
};

export default SidebarProfile;